import { makeAutoObservable, runInAction, toJS } from 'mobx';
import { RootStore } from 'store/rootStore';

import { ChecklistCard, ChecklistCompletionStatus, EnrollmentPeriod, USER_ACTIONS } from 'types/enrollmentService';
import { CopyHelper } from 'global/utils/CopyHelper';
import { INITIAL_ENROLLMENT_OBJECT } from 'store/modules/enrollment/enrollmentPeriodModule';
import { INITIAL_COMPLETE_ELECTION_CHECK, INITIAL_STATISTIC } from 'store/domain/electionStore';
import {
  Choice,
  CompleteElectionsCheck,
  DependentsChoice,
  ElectionStatus,
  EeClassWithValidation,
  INITIAL_CHOICE,
  ProductInfo,
  RateStatistic,
  RequiredSubInfo,
  RequiredSubInfoPresenter,
  SavingStatus,
  SelectedPlanInProductLine,
  StatisticsValues,
  Subscriber,
  SubscribersFilter,
  SubscribersSortBy,
  SubscribersSortOrder,
  UpdatedPlanInfo,
} from 'types/electionService';
import { DomainProductLine } from 'types/domainServiceV2';
import { requestApplyUserAction } from 'api/operations/enrollmentService';
import {
  getEmployeeClasses,
  getProducts,
  getRequiredSubInfo,
  getStatistics,
  getSubscribers,
  updateSubscribers,
} from 'api/operations/electionService';
import { sortDependentsInSubscriber } from 'global/services-helpers/electionService';
import { createRequiredSubInfoPresenter } from 'global/utils/election/electionPresenters';
import EditPrimaryModalStore from './editPrimaryModalStore';
import ManageDependentsModalStore from './manageDependentsStore';

export enum ActiveSorting {
  ASC = 'ASC',
  DESC = 'DESC',
  ELECTED_FIRST = 'ELECTED_FIRST',
  ELECTED_LAST = 'ELECTED_LAST',
}

export class EmployeeElectionsStore {
  rootStore: RootStore;
  subscribers: Subscriber[] = [];
  statistic: StatisticsValues = INITIAL_STATISTIC;
  rateStats: RateStatistic | null;
  loading = false;
  modalLoading = false;
  savingStatus: SavingStatus = 'SUCCESSFUL_SAVING';
  selectedSubscribersIds: string[] = [];
  isElectionCompleted: boolean | null = null;
  isElectionWithSubs = false;
  periodId = '';
  products: ProductInfo[] = [];
  employeeClasses: EeClassWithValidation[] = [];
  _requiredSubInfo: RequiredSubInfo | null = null;

  electionChangeStatuses: ElectionStatus[] = [];
  electionChangeReasons: string[] = [];
  productLines: DomainProductLine[] = [];

  public editPrimaryModalStore: EditPrimaryModalStore;
  public manageDependentsModalStore: ManageDependentsModalStore;

  // Remove subscriber modal info
  selectedSubscriber: Subscriber | null = null;
  isRemoveSubscriberModalOpen = false;

  // Check if the election data is valid to enable the end of open enrollment
  completeElectionsCheck: CompleteElectionsCheck = INITIAL_COMPLETE_ELECTION_CHECK;
  enrollmentPeriod: EnrollmentPeriod;

  // filter/sort/pagination
  filter: SubscribersFilter = SubscribersFilter.ALL;
  activeSortValue: ActiveSorting = ActiveSorting.ASC;
  sortOrder: SubscribersSortOrder = SubscribersSortOrder.ASC;
  sortBy: SubscribersSortBy = SubscribersSortBy.NAME;
  searchValue = '';
  currentPage = 1;
  totalPages = 1;
  totalElements = 1;
  statusesFilter: ElectionStatus[] = [];
  classesFilter: string[] = [];

  public get requiredSubInfo(): RequiredSubInfo | null {
    return this._requiredSubInfo;
  }

  /**
   * Enrollment required-subscriber rules for SubscriberCard
   */
  public get requiredSubInfoPresenter(): RequiredSubInfoPresenter | undefined {
    return createRequiredSubInfoPresenter(toJS(this._requiredSubInfo), 'enrollment') ?? undefined;
  }

  private setLoading(status: boolean): void {
    this.loading = status;
  }

  public setProducts(products: ProductInfo[]): void {
    this.products = products;
  }

  public openRemoveSubscriberModal(subscriberId: string): void {
    const currentSubscriber = this.getSubscriberById(subscriberId);

    if (currentSubscriber) {
      this.selectedSubscriber = currentSubscriber;
      this.isRemoveSubscriberModalOpen = true;
    }
  }

  public closeRemoveSubscriberModal(): void {
    this.isRemoveSubscriberModalOpen = false;
    this.selectedSubscriber = null;
  }

  private setModalLoading(status: boolean): void {
    this.modalLoading = status;
  }

  private setSavingStatus(status: SavingStatus): void {
    this.savingStatus = status;
  }

  public editPrimaryInfo(subscriberId: string): void {
    const formId = this.requiredSubInfo?.editSubFormId;
    if (!formId) {
      throw new Error('Edit primary form ID is missing');
    }
    void this.editPrimaryModalStore.openEditPrimaryModal(subscriberId, formId);
  }

  public manageDependents(subscriberId: string): void {
    const formId = this.requiredSubInfo?.manageDependentFormId;
    if (!formId) {
      throw new Error('Manage dependents form ID is missing');
    }
    void this.manageDependentsModalStore.openManageDependentsModal(subscriberId, formId);
  }

  public async uncompleteElections(): Promise<void> {
    this.setLoading(true);
    await requestApplyUserAction(this.periodId, USER_ACTIONS.UNCOMPLETE_ELECTIONS);
    runInAction(() => {
      this.updateElectionStatus(false);
      this.setLoading(false);
    });
  }

  // Filter/sort/pagination setters
  public updateFilter(filterValue: SubscribersFilter): void {
    this.filter = filterValue;
    this.updateCurrentPageNumber(1);
  }

  public updateActiveSortValue(sortValue: ActiveSorting): void {
    this.activeSortValue = sortValue;
    this.updateCurrentPageNumber(1);
  }

  public updateSearchValue(searchValue: string): void {
    this.searchValue = searchValue;
    this.updateCurrentPageNumber(1);
  }

  public updateSortOrder(sortOrderValue: SubscribersSortOrder): void {
    this.sortOrder = sortOrderValue;
    this.updateCurrentPageNumber(1);
  }

  public updateSubscribersSortBy(sortByValue: SubscribersSortBy): void {
    this.sortBy = sortByValue;
    this.updateCurrentPageNumber(1);
  }

  public updateCurrentPageNumber(updatedCurrentPage: number): void {
    this.currentPage = updatedCurrentPage;
  }

  // Manage subscribers selection methods
  public setAllSubscribersSelected(): void {
    this.selectedSubscribersIds = this.subscribers.map(({ id }) => id);
  }

  public setCurrentSubscriberSelected(subscriberId: string): void {
    this.selectedSubscribersIds.push(subscriberId);
  }

  public removeCurrentSelectedSubscriber(subscriberId: string): void {
    this.selectedSubscribersIds = this.selectedSubscribersIds.filter((id) => subscriberId !== id);
  }

  public clearAllSelectedSubscribers(): void {
    this.selectedSubscribersIds = [];
  }

  // Helpers
  private getSubscribers(): Subscriber[] {
    return this.subscribers?.map((subscriber) => toJS(subscriber)).slice();
  }

  private createInitialChoicesList(subscriber: Subscriber): Choice[] {
    const defaultDependentsList: string[] = subscriber.custom.dependents.map(({ id }) => id);

    if (this.products.length === 0) {
      return [];
    }

    const defaultInitialChoicesList: Choice[] = this.products.map(({ productLineId }) => {
      // productLine with 1 tier needs dependentIds as empty array
      const hasProductLineOneTier = Object.keys(this.rateStats?.[productLineId] || []).length === 1;
      return { ...INITIAL_CHOICE, productLineId, dependentIds: hasProductLineOneTier ? [] : defaultDependentsList };
    });

    return defaultInitialChoicesList;
  }

  // Generates default choices per product line and merges subscriber’s existing choices.
  // Handles cases where fewer choices are returned than product lines.
  private buildChoicesForSubscriber(subscriber: Subscriber): Choice[] {
    const initialChoices = this.createInitialChoicesList(subscriber);
    const populatedChoices = subscriber.choice ?? [];

    const choicesByProductLine = new Map(populatedChoices.map((choice) => [choice.productLineId, choice]));

    return initialChoices.map((initialChoice) => ({
      ...initialChoice,
      ...choicesByProductLine.get(initialChoice.productLineId),
    }));
  }

  public getSubscriberTotalPremium(subId: string): number {
    const convertedStatistic = toJS(this.statistic);
    const subRates = convertedStatistic?.subRates;
    const subscriberRates = subRates?.[subId] || [];
    return subscriberRates.reduce((total, rateInfo) => total + +rateInfo.rate, 0);
  }

  // eslint-disable-next-line class-methods-use-this
  public subscribersTotalElementsRange(currentPage: number, totalElements: number, elementPerPage = 12): string {
    const elementsTo = currentPage * elementPerPage <= totalElements ? currentPage * elementPerPage : totalElements;
    const elementsFrom = currentPage * elementPerPage - (elementPerPage - 1);
    return `${elementsFrom} - ${elementsTo}`;
  }

  public resetInitialData(): void {
    this.statistic = INITIAL_STATISTIC;
    this.searchValue = '';
    this.subscribers = [];
    this.statusesFilter = [];
    this.classesFilter = [];
    this.products = [];
    this.employeeClasses = [];
    this._requiredSubInfo = null;
    this.updateCurrentPageNumber(1);
  }

  public get cartId(): string {
    return this.enrollmentPeriod.proposals.find((proposal) => proposal.confirmed)?.cartId || '';
  }

  // Get data requests
  public async getSubscribersList(
    groupId: string,
    periodId: string,
    subscribersSortBy: SubscribersSortBy,
    subscribersSortOrder: SubscribersSortOrder,
    subscribersFilter: SubscribersFilter,
    searchValue: string,
    electionChangeStatuses: ElectionStatus[],
    employeeClasses: string[],
    page = 1,
  ): Promise<void> {
    this.setLoading(true);
    this.periodId = periodId;
    const domainId = this.rootStore.domainStoreV2.domainHeader.id;
    const convertedClasses = this.employeeClasses.map((eeClass) => toJS(eeClass)).slice();
    const convertedRequiredSubInfo = toJS(this._requiredSubInfo);

    if (this.products.length === 0) {
      const products = await getProducts(periodId);
      runInAction(() => {
        this.products = products;
      });
    }
    if (convertedClasses.length === 0) {
      const classes = await getEmployeeClasses(groupId, periodId);
      runInAction(() => {
        this.employeeClasses = classes;
      });
    }

    if (!convertedRequiredSubInfo) {
      const requiredSubInfo = await getRequiredSubInfo(domainId);
      runInAction(() => {
        this._requiredSubInfo = requiredSubInfo;
      });
    }

    const subscribersResult = await getSubscribers(
      groupId,
      periodId,
      subscribersSortBy,
      subscribersSortOrder,
      subscribersFilter,
      searchValue,
      electionChangeStatuses,
      employeeClasses,
      page,
    );
    runInAction(() => {
      this.totalPages = subscribersResult.totalPages;
      this.totalElements = subscribersResult.totalElements;
      const subscribersList = subscribersResult.result;

      const convertedSubscribers = subscribersList.map((subscriber): Subscriber => {
        return sortDependentsInSubscriber({
          ...subscriber,
          choice: this.buildChoicesForSubscriber(subscriber),
        });
      });
      this.subscribers = convertedSubscribers;
      this.setLoading(false);
    });
  }

  public async getStatistic(groupId: string, periodId: string, changeLoaderStatus = true): Promise<void> {
    if (changeLoaderStatus) this.setLoading(true);
    const res = await getStatistics(groupId, periodId);
    runInAction(() => {
      const { rateStats, ...restStatistic } = res;
      this.statistic = restStatistic;
      this.rateStats = rateStats;
      this.isElectionWithSubs = this.statistic.total !== 0;
      if (changeLoaderStatus) this.setLoading(false);
    });
  }

  private getSubscriberById(subscriberId: string): Subscriber | undefined {
    // Avoid converting subscribers to plain JavaScript array here as it won't trigger subsequent updates.
    return this.subscribers.find((subscriber) => subscriber.id === subscriberId);
  }

  // Update data requests
  public updateDependents(groupId: string, dependentsChoice: DependentsChoice): void {
    const { subscriberId, productLineId, dependentIds } = dependentsChoice;
    const currentSubscriber = this.getSubscriberById(subscriberId);

    const currentChoice = currentSubscriber
      ? this.rootStore.subscriberStore.getCurrentChoice(currentSubscriber, productLineId)
      : null;

    if (currentChoice) {
      currentChoice.dependentIds = dependentIds;
    }

    void this.updateElectionChanges(groupId);
  }

  public async removeSubscriberById(groupId: string, subscriberId: string, periodId: string): Promise<void> {
    const updatedSubscribers = this.subscribers?.filter(({ id }) => subscriberId !== id);
    this.subscribers = updatedSubscribers;
    await this.rootStore.electionStore.removeSubscriber(subscriberId);
    void this.getStatistic(groupId, periodId);
    void this.getSubscribersList(
      groupId,
      this.periodId,
      this.sortBy,
      this.sortOrder,
      this.filter,
      this.searchValue,
      this.statusesFilter,
      this.classesFilter,
      this.currentPage,
    );
  }

  public updateSelectedPlan(selectedPlanInfo: UpdatedPlanInfo): void {
    const { subscriberId, productLineId, selectedPlanName, selectedPlanId } = selectedPlanInfo;

    const currentSubscriber = this.getSubscriberById(subscriberId);

    const currentChoice = currentSubscriber
      ? this.rootStore.subscriberStore.getCurrentChoice(currentSubscriber, productLineId)
      : null;

    if (currentChoice && selectedPlanName) {
      currentChoice.productId = selectedPlanId;
      currentChoice.productName = selectedPlanName;
    }
  }

  public async updateElectionChanges(groupId: string): Promise<void> {
    this.setSavingStatus('SAVING');
    await updateSubscribers(this.periodId, this.getSubscribers())
      .then(() => {
        void this.getStatistic(groupId, this.periodId);
        this.setSavingStatus('SUCCESSFUL_SAVING');
      })
      .catch(() => this.setSavingStatus('FAILED_SAVING'));
  }

  public applyBatchChanges(groupId: string, selectedPlansInProductLine: SelectedPlanInProductLine[]): void {
    // Update plan choice on FE
    this.selectedSubscribersIds.forEach((subscriberId) => {
      selectedPlansInProductLine.forEach((planInfo) => {
        this.updateSelectedPlan({ subscriberId, ...planInfo });
      });
    });
    void this.updateElectionChanges(groupId); // Send updated subs to BE
  }

  public async submitElection(groupId: string, periodId: string): Promise<void> {
    this.setLoading(true);
    await Promise.all([
      await updateSubscribers(periodId, this.subscribers),
      requestApplyUserAction(periodId, USER_ACTIONS.COMPLETE_ELECTIONS),
      this.rootStore.electionStore.submitElection(groupId, periodId),
    ]);
    this.updateElectionStatus(null);
    this.setLoading(false);
  }

  public updateElectionStatus(status: boolean | null): void {
    this.isElectionCompleted = status;
  }

  public async checkElectionForCompleted(periodId: string): Promise<void> {
    this.updateElectionStatus(null);
    this.setLoading(true);
    await this.rootStore.enrollmentStore.getEnrollmentPeriod(periodId);

    void runInAction(() => {
      this.enrollmentPeriod = CopyHelper.deepCopy(this.rootStore.enrollmentStore.enrollmentPeriod);
      this.rootStore.subscriberStore.setPeriodEditableStatus(this.enrollmentPeriod);

      runInAction(() => {
        const electionStatus = this.enrollmentPeriod.checklist?.checklistOptions.find(
          (option) => option.card === ChecklistCard.EMPLOYEE_ELECTIONS,
        )?.completionStatus;
        this.updateElectionStatus(electionStatus === ChecklistCompletionStatus.COMPLETED);
        this.setLoading(false);
      });
    });
  }

  public updateClassAndResetProductSelection(subscriberId: string, className: string): void {
    const currentSubscriber = this.getSubscriberById(subscriberId);
    if (currentSubscriber) {
      const updatedChoice = currentSubscriber.choice.map((choice) => {
        return { ...choice, productId: '', productName: '' };
      });
      currentSubscriber.employeeClass = className;
      currentSubscriber.choice = updatedChoice;
    }
  }

  public applyClassesAndResetProductSelection(eeClassName: string): void {
    this.selectedSubscribersIds.forEach((subscriberId) => {
      this.updateClassAndResetProductSelection(subscriberId, eeClassName);
    });
  }

  public async checkElection(groupId: string, periodId: string): Promise<void> {
    this.setModalLoading(true);
    const res = await this.rootStore.electionStore.checkElection(groupId, periodId);
    runInAction(() => {
      this.completeElectionsCheck = res.data.getSubsValidations;
      this.setModalLoading(false);
    });
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.editPrimaryModalStore = new EditPrimaryModalStore(this.rootStore);
    this.manageDependentsModalStore = new ManageDependentsModalStore(this.rootStore);
    this.subscribers = [];
    this.selectedSubscribersIds = [];
    this.statistic = INITIAL_STATISTIC;
    this.rateStats = null;
    this.enrollmentPeriod = INITIAL_ENROLLMENT_OBJECT;

    makeAutoObservable(this, { rootStore: false }, { autoBind: true });
  }
}
